const STATE_STORAGE_KEY = "oauth_state";
const STATE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

interface OAuthState {
  state: string;
  identityProviderId: string;
  timestamp: number;
  returnUrl?: string;
  codeVerifier?: string; // PKCE code_verifier
  linkMode?: boolean; // If true, link to current user instead of creating new one
}

// Generate a cryptographically secure random state value
function generateSecureState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

// Generate a cryptographically secure random code_verifier for PKCE (RFC 7636)
// Returns a URL-safe base64 string (43-128 characters)
function generateCodeVerifier(): string {
  const array = new Uint8Array(32); // 256 bits = 32 bytes
  crypto.getRandomValues(array);
  // Convert to base64url (URL-safe base64 without padding)
  return base64UrlEncode(array);
}

// Generate code_challenge from code_verifier using SHA-256
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

// Base64URL encoding (RFC 4648 base64url without padding)
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Store OAuth state and PKCE parameters in sessionStorage
// Returns both state and codeChallenge for use in authorization URL
export async function storeOAuthState(
  identityProviderId: string,
  returnUrl?: string,
  linkMode?: boolean,
): Promise<{ state: string; codeChallenge: string }> {
  const state = generateSecureState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const stateData: OAuthState = {
    state,
    identityProviderId,
    timestamp: Date.now(),
    returnUrl,
    codeVerifier, // Store for later retrieval in callback
    linkMode, // Store link mode flag
  };

  try {
    sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(stateData));
  } catch (error) {
    console.error("Failed to store OAuth state:", error);
    throw new Error("Failed to initialize OAuth flow");
  }

  return { state, codeChallenge };
}

// Validate and retrieve OAuth state from storage (CSRF protection)
// Returns identityProviderId, returnUrl, codeVerifier for PKCE, and linkMode flag
export function validateOAuthState(
  stateParam: string,
): { identityProviderId: string; returnUrl?: string; codeVerifier?: string; linkMode?: boolean } | null {
  try {
    console.log("[OAuth] Validating state param:", stateParam ? `${stateParam.substring(0, 8)}...` : "null/empty");

    const storedData = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!storedData) {
      console.error("[OAuth] No OAuth state found in storage. Possible causes:");
      console.error("  - Browser cleared sessionStorage (privacy settings, extensions)");
      console.error("  - OAuth flow started in a different tab/window (sessionStorage is tab-specific)");
      console.error("  - Long delay between initiating OAuth and callback (sessionStorage cleared)");
      return null;
    }

    const stateData: OAuthState = JSON.parse(storedData);
    console.log("[OAuth] Stored state data:", {
      state: `${stateData.state.substring(0, 8)}...`,
      identityProviderId: stateData.identityProviderId,
      timestamp: stateData.timestamp,
      timeElapsed: `${Date.now() - stateData.timestamp}ms`,
      returnUrl: stateData.returnUrl,
      hasCodeVerifier: !!stateData.codeVerifier,
      linkMode: stateData.linkMode,
    });

    // Check if state has expired
    const age = Date.now() - stateData.timestamp;
    if (age > STATE_EXPIRY_MS) {
      console.error(`[OAuth] State has expired. Age: ${age}ms, Max: ${STATE_EXPIRY_MS}ms`);
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      return null;
    }

    // Validate state matches (CSRF protection)
    if (stateData.state !== stateParam) {
      console.error("[OAuth] State mismatch - possible CSRF attack or incorrect state parameter");
      console.error("  Expected:", stateData.state);
      console.error("  Received:", stateParam);
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      return null;
    }

    // State is valid, clean up and return data
    console.log("[OAuth] State validated successfully");
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    return {
      identityProviderId: stateData.identityProviderId,
      returnUrl: stateData.returnUrl,
      codeVerifier: stateData.codeVerifier, // Return PKCE code_verifier
      linkMode: stateData.linkMode, // Return link mode flag
    };
  } catch (error) {
    console.error("[OAuth] Failed to validate state:", error);
    sessionStorage.removeItem(STATE_STORAGE_KEY);
    return null;
  }
}

// Clean up expired OAuth states (call on app init)
export function cleanupExpiredOAuthState(): void {
  try {
    const storedData = sessionStorage.getItem(STATE_STORAGE_KEY);
    if (!storedData) {
      return;
    }

    const stateData: OAuthState = JSON.parse(storedData);
    if (Date.now() - stateData.timestamp > STATE_EXPIRY_MS) {
      sessionStorage.removeItem(STATE_STORAGE_KEY);
    }
  } catch {
    // If parsing fails, remove the corrupted data
    sessionStorage.removeItem(STATE_STORAGE_KEY);
  }
}
